import React from 'react';
import { observer } from 'mobx-react';
import { computed, observable } from 'mobx';
import ErrorBoundary from 'components/hoc/error_boundary.js';
import styled from '@emotion/styled';
import { MembershipTree2Icon, MembershipTree1Icon, Membership3LeafIcon, Membership2LeafIcon } from '@seedlang/icons';
import { flexCenter } from '@seedlang/style_mixins';
import DiscountEmblem from 'components/discount_emblem';
import { dollar, cents , isPresent, isBlank } from '@seedlang/utils';
import Text from 'components/text';
import { AppUI, LoadingState } from '@seedlang/state';
import ExpandableTextDisplay from 'components/expandable_text_display';
import { Theme } from '@seedlang/constants';
import Spinner from 'components/spinner';
import { pixify as px } from '@seedlang/utils/src';

const Wrapper = styled.div`
  position: relative;
  transition: all 0.2s ease;
  cursor: pointer;
  background: linear-gradient(-134deg, #1fcc64 0%, #45775e 100%);
  margin: 5px;
  border: ${props => props.border};
  min-width: ${props => props.width};
  max-width: ${props => props.width};
  padding: 15px;
  display: flex;
  flex-basis: calc(50% - 10px);
  align-items: center;
  flex-direction: column;
  border-radius: 20px;
  max-height: 365px;
  &:hover {
    transform: scale(1.05);
  }
`;

const MembershipTypeIcon = styled.div`
  ${flexCenter()}
  width: 100%;
  margin-bottom: 30px;
  .icon-wrapper {
    svg {
      width: 68px;
    }
  }
`;

const TopSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 10px;
`;

const AmountSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
`;

const Amount = styled.div`
  font-weight: bold;
  color: white;
  text-shadow: 1px 1px rgba(49, 49, 49, 0.8);
  height: 46px;
`;

const AmountWrapper = styled.div`
  display: flex;
`;

const CurrencySymbol = styled.div`
  font-size: 36px;
  margin-top: -4px;
`;

const DollarAmount = styled.div`
  font-size: 60px;
`;

const CentsAmount = styled.div`
  font-size: 23px;
  margin-top: -8px;
`;

const Slash = styled.div`
  font-size: 20px;
`;

const Month = styled.div`
  font-size: 15px;
`;

const NotesWrapper = styled.div`
  display: flex;
  align-items: center;
`;

const Description = styled.div`
  font-size: 13px;
  color: rgba(255, 255, 255, 0.9);
  flex-direction: column;
  margin-top: 2.5px;
  line-height: 15px;
  margin-bottom: 2.5px;
  text-align: center;
  text-wrap: balance;
  display: grid;
  place-items: center;
`;

const FullDescription = styled.div`
  width: 100%;
  flex-direction: column;
  line-height: 20px;
  margin-top: 10px;
  margin-bottom: 20px;
  overflow: hidden;
  p {
    color: white;
  }
  ul {
    margin-left: 20px;
  }
  li {
    list-style-type: disc;
  }
  a {
    text-decoration: underline;
  }
  a:hover {
    text-decoration: none;
  }
`;

const ButtonWrapper = styled.div`
  width: 100%;
  text-align: center;
  margin: 20px 0 10px 0;
  a {
    color: white;
    width: 100%;
  }
`;

const Button = styled.div`
  background: white;
  font-size: 16px;
  padding: 25px;
  line-height: 0;
  width: 100%;
  color: #3b9c61;
  border-radius: 50px;
  margin: 0;
  height: 50px;
  font-weight: bold;
  cursor: pointer;
  &:hover {
    transform: scale(1.05);
  }
`;

const Title = styled.div`
  margin-bottom: 5px;
  min-height: 50px;
  display: flex;
  align-items: center;
`;

const TopBanner = styled.div`
  background: ${props => props.background};
  position: absolute;
  border-radius: 10px;
  font-size: 14px;
  font-weight: bold;
  color: #FFF;
  top: -24px;
  padding: 10px 20px;
`;

@observer
class MembershipOptionDefault extends React.Component {
  @observable expanded = false;

  buttonLoadingState = new LoadingState();

  @computed get buttonText() {
    if (AppUI.user.signedOut) {
      return 'Get Started';
    } else if (this.props.membership.recurring) {
      return 'Subscribe';
    }
    return 'Buy Now';
  }

  @computed get border() {
    return this.props.membership.highlight ? `10px solid ${Theme.purple}` : '10px solid #408f60';
  }

  @computed get membershipName() {
    if (isPresent(this.props.membership?.membershipType?.name)) {
      return this.props.membership?.membershipType?.name;
    } else {
      return this.props.membership.title || '';
    }
  }

  @computed get description() {
    return this.props.currency?.toUpperCase() === 'USD' ? this.props.membership.descriptionUsd : this.props.membership.descriptionEur;
  }

  @computed get width() {
    return AppUI.layout.isMobile ? px(AppUI.layout.viewportWidth - 40) : '290px';
  }

  @computed get discountPercentage() {
    if (!this.props.membership || this.props.membership.numberMonths <= 1) return;
    const compareMembership = this.props.memberships?.find(
      item => item.numberMonths === 1 && item.membershipTypeId === this.props.membership.membershipTypeId,
    );
    if (!compareMembership) return;
    const currentMonthlyCost = this.props.membership.costMonthly(this.props.currency);
    const compareMonthlyCost = compareMembership.costMonthly(this.props.currency);
    return Math.floor(((compareMonthlyCost - currentMonthlyCost) / compareMonthlyCost) * 100);
  }

  render() {
    return (
      <Wrapper
        className='MembershipOption__Wrapper'
        onClick={() => this.props.onClick(this.props.membership)}
        border={this.border}
        width={this.width}
      >
        {
          this.props.membership.highlight &&
            <TopBanner background={Theme.purple}>{this.props.membership.highlightText}</TopBanner>
        }
        <TopSection>
          <Title>
            <Text
              fontSize='22px'
              bold
              color='white'
              textAlign='center'
              maxWidth='180px'
            >
              <div
                dangerouslySetInnerHTML={{ __html: this.membershipName }}
              />
            </Text>
          </Title>
          <MembershipTypeIcon >
            {
              this.props.membership.icon === '2-leaf' &&
                <Membership2LeafIcon />
            }
            {
              this.props.membership.icon === '3-leaf' &&
                <Membership3LeafIcon />
            }
            {
              this.props.membership.icon === 'tree-1' &&
                <MembershipTree1Icon />
            }
            {
              this.props.membership.icon === 'tree-2' &&
                <MembershipTree2Icon />
            }
          </MembershipTypeIcon>
          <AmountSection>
            <Amount>
              <AmountWrapper>
                <CurrencySymbol>
                  {this.props.currencySymbol}
                </CurrencySymbol>
                <DollarAmount>
                  {this.props.membership.recurring ? dollar(this.props.membership.costMonthly(this.props.currency)) : dollar(this.props.membership.cost(this.props.currency))}
                </DollarAmount>
                <CentsAmount>
                  {this.props.membership.recurring ? cents(this.props.membership.costMonthly(this.props.currency)) : cents(this.props.membership.cost(this.props.currency))}
                </CentsAmount>
                {
                  this.props.membership.recurring &&
                    <Slash>
                      /
                    </Slash>
                }
                {
                  this.props.membership.recurring &&
                    <Month>
                      month
                    </Month>
                }
              </AmountWrapper>
            </Amount>
            <NotesWrapper>
              {
                isPresent(this.description) &&
                  <Description>
                    {this.description}
                  </Description>
              }
            </NotesWrapper>
          </AmountSection>
          {
            this.discountPercentage &&
              <DiscountEmblem
                amount={this.discountPercentage}
                background={this.props.membership.highlight ? Theme.purple : '#408f60'}
              />
          }
        </TopSection>
        {
          (!AppUI.user.hasPermanentGroupMembership) &&
            <ButtonWrapper>
              {
                this.buttonLoadingState.isLoading ? (
                  <Spinner/>
                ) : (
                  <Button>{this.buttonText}</Button>
                )
              }
            </ButtonWrapper>
        }
        <FullDescription>
          <ExpandableTextDisplay
            paragraphLimit={100}
            markdownText={this.props.membership.descriptionMd}
            htmlText={isPresent(this.props.membership.description) && isBlank(this.props.membership.descriptionMd) && this.props.membership.description}
          />
        </FullDescription>
      </Wrapper>
    );
  }
}

export default ErrorBoundary(MembershipOptionDefault);
